import { useEffect, useState } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { useAppSelector } from '@/store/hooks'
import SignInModal from '@/components/modals/SignInModal/SignInModal'

// Pull an invite param (`invite` token or `invite_email`) out of the /auth query, the
// saved deep-link destination (`next`), or sessionStorage.authNext — whichever carries
// it. Returns '' when absent.
const readInviteParam = (name: string, search: string, next?: string): string => {
    const fromQuery = (s: string): string => {
        const q = s.indexOf('?')
        const qs = q >= 0 ? s.slice(q) : s
        try {
            return new URLSearchParams(qs).get(name) ?? ''
        } catch {
            return ''
        }
    }
    const direct = fromQuery(search)
    if (direct) return direct
    if (next) {
        const fromNext = fromQuery(next)
        if (fromNext) return fromNext
    }
    try {
        const saved = sessionStorage.getItem('authNext')
        if (saved) return fromQuery(saved)
    } catch {
        // ignore (storage disabled)
    }
    return ''
}

// Standalone login / registration page at /auth (form on a grey background).
// A logged-out visitor opening a task/project link is sent here with a `next`
// location, remembered so signing in (email/password or Google, incl. its
// redirect) returns straight to the linked task/project. See the authNext
// handling in authSaga.loadUserData.
const AuthPage = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const auth = useAppSelector(state => state.auth)
    // globalLoading is true while the session is still resolving — on a fresh load,
    // and crucially while a returning Google redirect is being completed (the saga
    // calls getRedirectResult, then hard-redirects to the workspace). We show the
    // form only once it settles to logged-out, so the form never flashes mid-redirect.
    const globalLoading = useAppSelector(state => state.ui.globalLoading)

    const next = (location.state as { next?: string } | null)?.next
    // A new invitee's link carries an opaque invitation token; an existing user's link
    // carries the plaintext address. Resolve the token to the email (and record that the
    // link was opened — the proof onUserCreated trusts to skip verification).
    const inviteToken = readInviteParam('invite', location.search, next)
    const inviteEmailParam = readInviteParam('invite_email', location.search, next)
    const [resolvedEmail, setResolvedEmail] = useState('')

    useEffect(() => {
        if (!inviteToken) return
        let cancelled = false
        fetch(`/api/public/invitations/${encodeURIComponent(inviteToken)}`)
            .then(r => (r.ok ? r.json() : null))
            .then(data => { if (!cancelled && data?.email) setResolvedEmail(data.email) })
            .catch(() => { /* pre-fill is best-effort */ })
        return () => { cancelled = true }
    }, [inviteToken])

    // Persist where to return so it survives the login reload / Google redirect.
    useEffect(() => {
        if (next) {
            try {
                sessionStorage.setItem('authNext', next)
            } catch {
                // ignore (storage disabled)
            }
        }
    }, [next])

    // This branch doesn't mount GlobalLoadingManager, which normally lifts the
    // index.html splash. Keep the splash (our logo) up while the session resolves;
    // dismiss it only when we're actually showing the sign-in form.
    const showForm = auth === null && !globalLoading
    useEffect(() => {
        document.body.classList.toggle('--loaded', showForm)
    }, [showForm])

    // Already signed in (navigated here directly) — leave. After a sign-in initiated
    // here, loadUserData redirects to `next` before this would run.
    if (auth !== null) return <Navigate to="/" replace />

    // Still resolving the session (initial load / completing a Google redirect) —
    // keep the index.html logo splash up rather than flashing the form.
    if (globalLoading) return null

    // Pre-fill the email when arriving from an invite link: the token resolved to an
    // address, or the plaintext invite_email param for existing-user links.
    const initialEmail = resolvedEmail || inviteEmailParam

    return <SignInModal onClose={() => navigate('/', { replace: true })} canClose={false} initialEmail={initialEmail} />
}

export default AuthPage
